import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Loader2, Search, X } from "lucide-react";
import { apiFetch } from "@/lib/api-client";

interface GroupServiceOption {
  id: string;
  name: string;
  durationMinutes: number;
  maxParticipants: number | null;
  waitlistConfig: { mode: "auto_add" | "first_to_claim"; claimTimeoutMinutes?: number } | null;
}

interface CommunitySpaceOption {
  id: string;
  name: string;
}

interface ContactResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export interface GroupSessionFormValues {
  serviceId: string;
  personId: string;
  startAt: string;
  endAt: string;
  capacityOverride: number | null;
  locationAddress: string;
  customerNotes: string;
  communitySpaceId: string | null;
  recurrence: {
    pattern: string;
    preferredDay?: number;
    preferredTime?: string;
    timezone?: string;
    autoBook?: boolean;
  } | null;
}

interface GroupSessionFormProps {
  isOpen: boolean;
  mode: "create" | "edit";
  services: GroupServiceOption[];
  communitySpaces: CommunitySpaceOption[];
  initialValues: GroupSessionFormValues;
  isSubmitting: boolean;
  onClose(): void;
  onSubmit(values: GroupSessionFormValues): Promise<void>;
}

const RECURRENCE_PATTERNS = [
  { value: "none", label: "Does not repeat" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom cadence" },
] as const;

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
] as const;

function contactName(contact: ContactResult): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email || "Unknown";
}

export function toDateTimeLocalValue(value: string): string {
  const date = new Date(value);
  const offsetMinutes = date.getTimezoneOffset();
  const local = new Date(date.getTime() - (offsetMinutes * 60_000));
  return local.toISOString().slice(0, 16);
}

export function fromDateTimeLocalValue(value: string): string {
  return new Date(value).toISOString();
}

function deriveRecurrenceDefaults(
  startAt: string,
  recurrence: GroupSessionFormValues["recurrence"],
) {
  const start = new Date(startAt);
  return {
    pattern: recurrence?.pattern ?? "none",
    preferredDay: recurrence?.preferredDay ?? start.getDay(),
    preferredTime: recurrence?.preferredTime ?? start.toISOString().slice(11, 16),
    timezone: recurrence?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC",
    autoBook: recurrence?.autoBook ?? true,
  };
}

export function GroupSessionForm({
  isOpen,
  mode,
  services,
  communitySpaces,
  initialValues,
  isSubmitting,
  onClose,
  onSubmit,
}: GroupSessionFormProps) {
  const formId = useId();
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [serviceId, setServiceId] = useState(initialValues.serviceId);
  const [personId, setPersonId] = useState(initialValues.personId);
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState<ContactResult[]>([]);
  const [contactLabel, setContactLabel] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [startAt, setStartAt] = useState(toDateTimeLocalValue(initialValues.startAt));
  const [endAt, setEndAt] = useState(toDateTimeLocalValue(initialValues.endAt));
  const [capacityOverride, setCapacityOverride] = useState(
    initialValues.capacityOverride === null ? "" : String(initialValues.capacityOverride),
  );
  const [locationAddress, setLocationAddress] = useState(initialValues.locationAddress);
  const [customerNotes, setCustomerNotes] = useState(initialValues.customerNotes);
  const [communitySpaceId, setCommunitySpaceId] = useState(initialValues.communitySpaceId ?? "");
  const [recurrenceState, setRecurrenceState] = useState(
    deriveRecurrenceDefaults(initialValues.startAt, initialValues.recurrence),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setServiceId(initialValues.serviceId);
    setPersonId(initialValues.personId);
    setContactQuery("");
    setContactResults([]);
    setContactLabel("");
    setShowDropdown(false);
    setStartAt(toDateTimeLocalValue(initialValues.startAt));
    setEndAt(toDateTimeLocalValue(initialValues.endAt));
    setCapacityOverride(initialValues.capacityOverride === null ? "" : String(initialValues.capacityOverride));
    setLocationAddress(initialValues.locationAddress);
    setCustomerNotes(initialValues.customerNotes);
    setCommunitySpaceId(initialValues.communitySpaceId ?? "");
    setRecurrenceState(deriveRecurrenceDefaults(initialValues.startAt, initialValues.recurrence));
    setError(null);
  }, [initialValues]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!isOpen || personId || contactQuery.trim().length < 2) {
      if (!personId) {
        setContactResults([]);
      }
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const controller = new AbortController();
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await apiFetch<{ data: { items: ContactResult[] } }>(
          `/crm/people?search=${encodeURIComponent(contactQuery)}&limit=5`,
          { signal: controller.signal },
        );
        if (!controller.signal.aborted) {
          setContactResults(response.data.items ?? []);
          setShowDropdown(true);
        }
      } catch {
        if (!controller.signal.aborted) {
          setContactResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, 250);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      controller.abort();
    };
  }, [contactQuery, isOpen, personId]);

  if (!isOpen) {
    return null;
  }

  const selectedService = services.find((service) => service.id === serviceId) ?? null;
  const effectiveCapacity = capacityOverride.trim().length > 0
    ? Number.parseInt(capacityOverride, 10)
    : selectedService?.maxParticipants ?? null;

  const recurrenceEnabled = recurrenceState.pattern !== "none";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!serviceId) {
      setError("Choose a group service before saving.");
      return;
    }
    if (!personId) {
      setError("Choose the lead contact for this session.");
      return;
    }
    if (!startAt || !endAt) {
      setError("Set both a start and end time.");
      return;
    }
    if (new Date(startAt) >= new Date(endAt)) {
      setError("Start time must be before end time.");
      return;
    }

    setError(null);
    await onSubmit({
      serviceId,
      personId,
      startAt: fromDateTimeLocalValue(startAt),
      endAt: fromDateTimeLocalValue(endAt),
      capacityOverride: capacityOverride.trim().length > 0
        ? Number.parseInt(capacityOverride, 10)
        : null,
      locationAddress,
      customerNotes,
      communitySpaceId: communitySpaceId || null,
      recurrence: recurrenceEnabled
        ? {
          pattern: recurrenceState.pattern,
          preferredDay: recurrenceState.preferredDay,
          preferredTime: recurrenceState.preferredTime,
          timezone: recurrenceState.timezone,
          autoBook: recurrenceState.autoBook,
        }
        : null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4" onClick={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] shadow-[0_40px_120px_-55px_rgba(15,23,42,0.6)]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={mode === "create" ? "Create group session" : "Edit group session"}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--color-border)] px-6 py-5">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">
              {mode === "create" ? "New recurring class" : "Edit session"}
            </p>
            <h2 className="text-2xl font-semibold text-[var(--color-foreground)]">
              {mode === "create" ? "Create a group session" : "Update group session"}
            </h2>
            <p className="max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
              Configure the live group-session runtime: choose the service, set the schedule, define capacity, and keep the
              recurring cadence aligned with the service-delivery engine.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-[var(--color-border)] p-2 text-[var(--color-muted-foreground)] transition hover:text-[var(--color-foreground)]"
            aria-label="Close group session form"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form id={formId} onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
          {error ? (
            <div className="rounded-2xl border border-[var(--color-error)]/25 bg-[var(--color-error)]/8 px-4 py-3 text-sm text-[var(--color-error)]">
              {error}
            </div>
          ) : null}

          <div className="grid gap-6 lg:grid-cols-[1.25fr_1fr]">
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2">
                  <span className="text-sm font-medium text-[var(--color-foreground)]">Group service</span>
                  <select
                    value={serviceId}
                    onChange={(event) => setServiceId(event.target.value)}
                    className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                  >
                    <option value="">Select a group service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name} · {service.durationMinutes} min
                      </option>
                    ))}
                  </select>
                </label>

                <label className="space-y-2">
                  <span className="text-sm font-medium text-[var(--color-foreground)]">Effective capacity</span>
                  <input
                    type="number"
                    min="1"
                    value={capacityOverride}
                    onChange={(event) => setCapacityOverride(event.target.value)}
                    placeholder={selectedService?.maxParticipants ? String(selectedService.maxParticipants) : "Use service default"}
                    className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                  />
                </label>
              </div>

              <div ref={dropdownRef} className="space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">Lead contact</span>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                  <input
                    type="text"
                    value={personId ? contactLabel : contactQuery}
                    onChange={(event) => {
                      setPersonId("");
                      setContactLabel("");
                      setContactQuery(event.target.value);
                    }}
                    className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-11 pr-12 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                    placeholder="Search contacts by name or email"
                  />
                  {(personId || contactQuery) ? (
                    <button
                      type="button"
                      onClick={() => {
                        setPersonId("");
                        setContactLabel("");
                        setContactQuery("");
                        setContactResults([]);
                        setShowDropdown(false);
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                {showDropdown ? (
                  <div className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] shadow-lg">
                    {isSearching ? (
                      <div className="px-4 py-3 text-sm text-[var(--color-muted-foreground)]">Searching contacts…</div>
                    ) : contactResults.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-[var(--color-muted-foreground)]">No matching contacts found.</div>
                    ) : (
                      contactResults.map((contact) => (
                        <button
                          key={contact.id}
                          type="button"
                          onClick={() => {
                            setPersonId(contact.id);
                            setContactLabel(contactName(contact));
                            setContactQuery("");
                            setContactResults([]);
                            setShowDropdown(false);
                          }}
                          className="flex w-full items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-3 text-left first:border-t-0 hover:bg-[var(--color-surface)]"
                        >
                          <span className="text-sm font-medium text-[var(--color-foreground)]">{contactName(contact)}</span>
                          <span className="truncate text-xs text-[var(--color-muted-foreground)]">{contact.email ?? "No email"}</span>
                        </button>
                      ))
                    )}
                  </div>
                ) : null}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2">
                  <span className="text-sm font-medium text-[var(--color-foreground)]">Start time</span>
                  <input
                    type="datetime-local"
                    value={startAt}
                    onChange={(event) => {
                      setStartAt(event.target.value);
                      const nextStart = new Date(event.target.value);
                      if (!Number.isNaN(nextStart.getTime()) && new Date(endAt) <= nextStart) {
                        const nextEnd = new Date(nextStart.getTime() + 60 * 60_000);
                        setEndAt(toDateTimeLocalValue(nextEnd.toISOString()));
                      }
                      setRecurrenceState((current) => ({
                        ...current,
                        preferredDay: nextStart.getDay(),
                        preferredTime: event.target.value.slice(11, 16),
                      }));
                    }}
                    className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                  />
                </label>
                <label className="space-y-2">
                  <span className="text-sm font-medium text-[var(--color-foreground)]">End time</span>
                  <input
                    type="datetime-local"
                    value={endAt}
                    onChange={(event) => setEndAt(event.target.value)}
                    className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                  />
                </label>
              </div>

              <label className="space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">Room or location note</span>
                <input
                  type="text"
                  value={locationAddress}
                  onChange={(event) => setLocationAddress(event.target.value)}
                  placeholder="Studio A · Floor 2"
                  className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                />
              </label>

              {mode === "create" && communitySpaces.length > 0 ? (
                <label className="space-y-2">
                  <span className="text-sm font-medium text-[var(--color-foreground)]">Post to community</span>
                  <select
                    value={communitySpaceId}
                    onChange={(event) => setCommunitySpaceId(event.target.value)}
                    className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                  >
                    <option value="">Do not post this session</option>
                    {communitySpaces.map((space) => (
                      <option key={space.id} value={space.id}>{space.name}</option>
                    ))}
                  </select>
                  <p className="text-xs leading-5 text-[var(--color-muted-foreground)]">
                    When selected, Stero will publish a community announcement with the schedule and booking link after the session is created.
                  </p>
                </label>
              ) : null}

              <label className="space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">Operator notes</span>
                <textarea
                  value={customerNotes}
                  onChange={(event) => setCustomerNotes(event.target.value)}
                  rows={4}
                  placeholder="Anything staff should know before attendees arrive."
                  className="w-full rounded-[22px] border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                />
              </label>
            </div>

            <div className="space-y-5 rounded-[24px] border border-[var(--color-border)] bg-[var(--color-surface)]/55 p-5">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-[var(--color-foreground)]">Recurrence</p>
                <p className="text-xs leading-5 text-[var(--color-muted-foreground)]">
                  Group sessions can stay one-off or save a repeat cadence for weekly and monthly classes.
                </p>
              </div>

              <label className="space-y-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">Cadence</span>
                <select
                  value={recurrenceState.pattern}
                  onChange={(event) => setRecurrenceState((current) => ({ ...current, pattern: event.target.value }))}
                  className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                >
                  {RECURRENCE_PATTERNS.map((pattern) => (
                    <option key={pattern.value} value={pattern.value}>
                      {pattern.label}
                    </option>
                  ))}
                </select>
              </label>

              {recurrenceEnabled ? (
                <div className="space-y-4">
                  <label className="space-y-2">
                    <span className="text-sm font-medium text-[var(--color-foreground)]">Preferred day</span>
                    <select
                      value={recurrenceState.preferredDay}
                      onChange={(event) => setRecurrenceState((current) => ({ ...current, preferredDay: Number(event.target.value) }))}
                      className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                    >
                      {WEEKDAY_OPTIONS.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-2">
                    <span className="text-sm font-medium text-[var(--color-foreground)]">Preferred time</span>
                    <input
                      type="time"
                      value={recurrenceState.preferredTime}
                      onChange={(event) => setRecurrenceState((current) => ({ ...current, preferredTime: event.target.value }))}
                      className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                    />
                  </label>

                  <label className="space-y-2">
                    <span className="text-sm font-medium text-[var(--color-foreground)]">Timezone</span>
                    <input
                      type="text"
                      value={recurrenceState.timezone}
                      onChange={(event) => setRecurrenceState((current) => ({ ...current, timezone: event.target.value }))}
                      className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-sm text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                    />
                  </label>

                  <label className="flex items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3">
                    <input
                      type="checkbox"
                      checked={Boolean(recurrenceState.autoBook)}
                      onChange={(event) => setRecurrenceState((current) => ({ ...current, autoBook: event.target.checked }))}
                      className="h-4 w-4 rounded border-[var(--color-border)]"
                    />
                    <span className="text-sm text-[var(--color-foreground)]">Auto-book future instances on this cadence</span>
                  </label>
                </div>
              ) : null}

              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-4 text-sm text-[var(--color-muted-foreground)]">
                <p className="font-medium text-[var(--color-foreground)]">Runtime summary</p>
                <dl className="mt-3 space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <dt>Duration</dt>
                    <dd className="font-medium text-[var(--color-foreground)]">
                      {selectedService ? `${selectedService.durationMinutes} min` : "Choose a service"}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt>Capacity</dt>
                    <dd className="font-medium text-[var(--color-foreground)]">
                      {effectiveCapacity ?? "Flexible"}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt>Waitlist</dt>
                    <dd className="font-medium text-[var(--color-foreground)]">
                      {selectedService?.waitlistConfig ? selectedService.waitlistConfig.mode.replace("_", " ") : "Disabled"}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-[var(--color-border)] px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-[var(--color-border)] px-4 py-2.5 text-sm font-medium text-[var(--color-foreground)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            form={formId}
            disabled={isSubmitting}
            className="inline-flex min-w-[170px] items-center justify-center gap-2 rounded-2xl bg-[var(--color-primary)] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {mode === "create" ? "Create group session" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
